/**
 * Account session — plan 16 MVP (Google sign-in, account wajib).
 *
 * Menyimpan opaque session token dari POST /v1/auth/google beserta
 * profile account. Token dikirim sebagai `Authorization: Bearer <token>`
 * oleh apiFetch (lib/api/client.ts).
 *
 * Storage:
 *  - Web (PWA MVP): localStorage under STORAGE_KEY.
 *  - Native: in-memory only untuk MVP (SecureStore mengikuti pola
 *    parent-session.ts saat native build diaktifkan lagi).
 *
 * Token BUKAN PII anak, tapi tetap treat sebagai secret — jangan di-log.
 *
 * Catatan: sengaja tanpa import react-native (Platform) supaya module
 * tetap testable di vitest Node pool — pakai feature-detection seperti
 * device-id.ts.
 *
 * Doc: tm-ai-workspace/plan/funpaper-app/16-mvp-pwa-free-launch.md (Task A6)
 */

const STORAGE_KEY = 'funpaper.auth_session.v1';
/**
 * Penanda pernah login akun asli (bukan guest) di browser ini. Sengaja
 * TIDAK dihapus oleh clearAuthSession/logout — dipakai entry onboarding
 * untuk mengantar user lama langsung ke /login, bukan mengulang onboarding.
 */
const EVER_AUTH_STORAGE_KEY = 'funpaper.auth_ever.v1';

export interface AuthAccount {
  id: number;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  /** plan 17: true untuk shadow guest account (mode tamu). */
  is_guest?: boolean;
}

export interface StoredAuthSession {
  token: string;
  account: AuthAccount;
}

/**
 * In-memory cache supaya apiFetch bisa inject Bearer token secara sync.
 * `undefined` = belum dibaca dari storage; `null` = tidak ada sesi.
 */
let cache: StoredAuthSession | null | undefined;

function isStoredSession(value: unknown): value is StoredAuthSession {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as StoredAuthSession;
  return (
    typeof v.token === 'string' &&
    v.token.length >= 10 &&
    typeof v.account === 'object' &&
    v.account !== null &&
    typeof v.account.id === 'number' &&
    typeof v.account.email === 'string'
  );
}

function readStorage(): StoredAuthSession | null {
  if (typeof window !== 'undefined') {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      return isStoredSession(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Sync read — dipakai apiFetch untuk Bearer injection. */
export function getAuthSession(): StoredAuthSession | null {
  if (cache === undefined) cache = readStorage();
  return cache;
}

export function getAuthToken(): string | null {
  return getAuthSession()?.token ?? null;
}

export function getStoredAccount(): AuthAccount | null {
  return getAuthSession()?.account ?? null;
}

/** true bila sesi saat ini adalah guest (mode tamu), false/null bila bukan/tidak ada sesi. */
export function isGuestSession(): boolean {
  return getStoredAccount()?.is_guest === true;
}

export function saveAuthSession(session: StoredAuthSession): void {
  cache = session;
  if (session.account.is_guest !== true) markEverAuthenticated();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      // localStorage disabled (private mode / quota) — sesi hanya hidup
      // di memori untuk launch ini; user harus login ulang setelah reload.
    }
  }
}

function markEverAuthenticated(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(EVER_AUTH_STORAGE_KEY, '1');
  } catch {
    // localStorage disabled — flag hanya hidup di memori untuk launch ini.
  }
}

/**
 * true bila browser ini pernah login akun asli (bukan guest), walaupun
 * sesinya sudah logout/hapus. Dipakai untuk memutuskan onboarding vs login.
 */
export function hasEverAuthenticated(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(EVER_AUTH_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Update the friendly name without exposing the auth token to UI code. */
export function updateStoredDisplayName(displayName: string | null): void {
  const current = getAuthSession();
  if (!current) return;
  const normalized = displayName?.trim() || null;
  saveAuthSession({
    ...current,
    account: { ...current.account, display_name: normalized },
  });
}

export function clearAuthSession(): void {
  cache = null;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

/** Untuk tests: reset cache tanpa menyentuh storage. */
export function __resetAuthCacheForTests(): void {
  cache = undefined;
}
